import React, { useEffect, useMemo, useState } from 'react';
import {
  ImagePlus, Info, Loader2, Plus, Save, Trash2, X,
} from 'lucide-react';
import client, { resolveApiAssetUrl } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import { UPLOAD_MAX_LABEL, uploadTooLarge } from '../../lib/mediaAsset';

// Kayıp bonusunun penceresi, net giriş eşiği ve dikey ayrımı.
// Değerler `settings` kaydında değil `provider_configs`'tedir (bkz.
// backend/services/cashback_rules.py) — o tablo InnoDB'nin satır sınırına
// dayanmıştır. Bu yüzden sayfanın ortak Kaydet düğmesine bağlanmaz: kendi
// ucu ve kendi düğmesi vardır (çekim kurallarındaki kalıp).

export const VERTICALS = [
  { key: 'sports', label: 'Spor', hint: 'Spor kuponlarının kaybı' },
  { key: 'casino', label: 'Casino', hint: 'Slot ve canlı casino turlarının kaybı' },
];

const EMPTY_VERTICAL = {
  enabled: true, tiers: [], max_bonus: null, min_payout: null,
  wager_multiplier: null, cover_image: '',
};

const IMAGE_ACCEPT = 'image/jpeg,image/png,image/gif,image/webp,image/svg+xml,.svg';

// Bonuslar sayfasındaki kayıp bonusu kartının kapağı (kampanya görseli
// alanıyla aynı kalıp). Boşken kart tema renkleriyle çizilen kapağı gösterir.
export function CoverImageField({
  label, hint, value, onChange, disabled, compact = false,
}) {
  const { toast } = useToast();
  const [uploading, setUploading] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    if (uploadTooLarge(file)) {
      toast({ title: 'Görsel yüklenemedi', description: `Dosya en fazla ${UPLOAD_MAX_LABEL} olabilir.` });
      return;
    }
    const formData = new FormData();
    formData.append('file', file);
    setUploading(true);
    try {
      const { data } = await client.post('/admin/uploads/images', formData);
      onChange(data.url);
      toast({ title: 'Görsel yüklendi', description: 'Kalıcı olması için Kuralları Kaydet düğmesine basın.' });
    } catch (error) {
      toast({ title: 'Yükleme başarısız', description: error.response?.data?.detail || 'Görsel yüklenemedi' });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <span className={compact ? 'text-xs font-semibold block mb-1.5' : 'settings-field-label'}>{label}</span>
      <div className={`grid gap-3 ${compact ? '' : 'sm:grid-cols-[170px_1fr]'}`}>
        <div className="relative aspect-[16/9] overflow-hidden rounded-xl border border-white/10 bg-slate-900">
          {value
            ? <img src={resolveApiAssetUrl(value)} alt={`${label} önizlemesi`} className="absolute inset-0 h-full w-full object-cover" />
            : <div className="absolute inset-0 grid place-items-center text-slate-600"><ImagePlus size={24} /></div>}
          {value && !disabled && (
            <button
              type="button"
              onClick={() => onChange('')}
              className="absolute right-1.5 top-1.5 grid h-7 w-7 place-items-center rounded-full bg-black/70 text-white"
              aria-label={`${label} kaldır`}
              title="Görseli kaldır"
            >
              <X size={14} />
            </button>
          )}
        </div>
        <div className="space-y-2">
          {!disabled && (
            <label className={`admin-btn-primary flex min-h-10 cursor-pointer items-center justify-center gap-2 ${uploading ? 'pointer-events-none opacity-60' : ''}`}>
              {uploading ? <Loader2 size={15} className="animate-spin" /> : <ImagePlus size={15} />} Görsel Seç
              <input
                type="file" accept={IMAGE_ACCEPT} className="sr-only" disabled={uploading}
                onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ''; }}
              />
            </label>
          )}
          <input
            value={value || ''} disabled={disabled}
            onChange={(e) => onChange(e.target.value.trim())}
            className="admin-input" placeholder="veya https:// görsel adresi"
            aria-label={`${label} adresi`}
          />
          {hint && <p className="text-[11px] leading-relaxed text-slate-500">{hint}</p>}
        </div>
      </div>
    </div>
  );
}

// Boş metin "kurulum geneli ayarı kullan" demektir; 0 gerçek bir değerdir
// ("limitsiz", "çevrimsiz"). İkisini aynı kutuda ayırmanın tek yolu budur.
export function parseInherited(raw) {
  const text = String(raw ?? '').trim();
  if (text === '') return null;
  const value = Number(text);
  return Number.isFinite(value) && value >= 0 ? value : null;
}

export function inheritedText(value) {
  return value === null || value === undefined ? '' : String(value);
}

export default function CashbackRulesSettings({ canWrite, period }) {
  const { toast } = useToast();
  const [saved, setSaved] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    client.get('/admin/cashback/config')
      .then(({ data }) => {
        if (!alive) return;
        setSaved(data);
        setDraft(data);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const dirty = useMemo(
    () => JSON.stringify(saved) !== JSON.stringify(draft),
    [saved, draft],
  );

  if (!draft) return null;

  const patch = (values) => setDraft({ ...draft, ...values });
  const vertical = (key) => ({ ...EMPTY_VERTICAL, ...(draft.verticals?.[key] || {}) });
  const patchVertical = (key, values) => patch({
    verticals: { ...draft.verticals, [key]: { ...vertical(key), ...values } },
  });

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await client.patch('/admin/cashback/config', {
        split_enabled: !!draft.split_enabled,
        rolling_hours: Number(draft.rolling_hours) || 24,
        min_net_funding: Number(draft.min_net_funding) || 0,
        count_pending_withdraw: !!draft.count_pending_withdraw,
        cover_image: draft.cover_image || '',
        verticals: Object.fromEntries(
          VERTICALS.map(({ key }) => [key, vertical(key)]),
        ),
      });
      setSaved(data);
      setDraft(data);
      toast({ title: 'Kayıp bonusu kuralları kaydedildi' });
    } catch (e) {
      toast({
        title: 'Kayıp bonusu kuralları kaydedilemedi',
        description: e.response?.data?.detail?.[0]?.msg
          || e.response?.data?.detail || 'Lütfen tekrar deneyin.',
      });
    } finally {
      setSaving(false);
    }
  };

  const tierRows = (key) => {
    const own = vertical(key);
    const setTiers = (tiers) => patchVertical(key, { tiers });
    return (
      <div className="space-y-2">
        {(own.tiers || []).map((t, i) => (
          <div key={i} className="flex items-center gap-2">
            <div className="flex-1">
              <label className="text-[11px] text-slate-500 block mb-1">Min. net kayıp (₺)</label>
              <input
                type="number" step="1" value={t.min_loss ?? 0} disabled={!canWrite}
                className="admin-input"
                onChange={(e) => {
                  const tiers = [...own.tiers];
                  tiers[i] = { ...tiers[i], min_loss: Number(e.target.value) };
                  setTiers(tiers);
                }}
              />
            </div>
            <div className="flex-1">
              <label className="text-[11px] text-slate-500 block mb-1">İade oranı (%)</label>
              <input
                type="number" step="0.5" value={t.percent ?? 0} disabled={!canWrite}
                className="admin-input"
                onChange={(e) => {
                  const tiers = [...own.tiers];
                  tiers[i] = { ...tiers[i], percent: Number(e.target.value) };
                  setTiers(tiers);
                }}
              />
            </div>
            {canWrite && (
              <button
                type="button" className="icon-btn ban mt-5" title="Kademeyi sil"
                onClick={() => setTiers(own.tiers.filter((_, x) => x !== i))}
              >
                <Trash2 size={13} />
              </button>
            )}
          </div>
        ))}
        {(own.tiers || []).length === 0 && (
          <div className="text-xs text-slate-500 py-2">
            Kademe yok — bu dikey kurulum geneli kademe tablosunu kullanır.
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="col-span-2 border-t border-white/5 pt-4 mt-2 space-y-4">
      <div className="text-sm font-semibold">Pencere, Eşik ve Dikey Ayrımı</div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="settings-field-label">Kayan Pencere Uzunluğu (saat)</label>
          <input
            type="number" min="1" max="720" step="1" disabled={!canWrite}
            value={draft.rolling_hours ?? 24}
            onChange={(e) => patch({ rolling_hours: Number(e.target.value) })}
            className="admin-input admin-input-w-sm"
            aria-label="Kayan pencere uzunluğu (saat)"
          />
          <p className="mt-1 text-xs text-slate-400">
            Yalnızca <b>Hesaplama Modeli = Kayan Pencere</b> seçiliyken kullanılır.
            {period !== 'rolling' && (
              <span className="text-amber-400"> Şu anki model takvim tabanlı.</span>
            )}
          </p>
        </div>
        <div>
          <label className="settings-field-label">Min. Net Para Girişi (₺)</label>
          <input
            type="number" min="0" step="1" disabled={!canWrite}
            value={draft.min_net_funding ?? 0}
            onChange={(e) => patch({ min_net_funding: Number(e.target.value) })}
            className="admin-input admin-input-w-sm"
            aria-label="Minimum net para girişi"
          />
          <p className="mt-1 text-xs text-slate-400">
            Pencere içinde <b>yatırım − çekim</b> bu tutarın altındaysa bonus
            doğmaz. 0 = kural kapalı.
          </p>
        </div>
      </div>

      <label className="flex items-start gap-2 text-sm">
        <input
          type="checkbox" disabled={!canWrite}
          checked={!!draft.count_pending_withdraw}
          onChange={(e) => patch({ count_pending_withdraw: e.target.checked })}
        />
        <span>
          Bekleyen çekim de düşülsün
          <span className="block text-xs text-slate-400">
            Onay bekleyen çekim bakiyeden zaten düşmüştür. Kapatırsanız yalnızca
            onaylanmış çekim sayılır ve bekleyen çekimi olan üye bonus alabilir.
          </span>
        </span>
      </label>

      <CoverImageField
        label="Bonuslar Sayfası Kart Görseli"
        value={draft.cover_image || ''}
        disabled={!canWrite}
        onChange={(url) => patch({ cover_image: url })}
        hint={`Kayıp bonusunun Bonuslar sayfasındaki kartında kampanya görseli gibi çizilir (Merit ve Betrun tasarımları). Kampanya görselleriyle aynı ölçüde yükleyin; boş bırakılırsa kart site renkleriyle kendi kapağını çizer. En fazla ${UPLOAD_MAX_LABEL}.`}
      />

      <label className="flex items-start gap-2 text-sm">
        <input
          type="checkbox" disabled={!canWrite}
          checked={!!draft.split_enabled}
          onChange={(e) => patch({ split_enabled: e.target.checked })}
        />
        <span>
          Spor ve casino ayrı bonuslansın
          <span className="block text-xs text-slate-400">
            Açıkken oyuncu iki ayrı kart görür ve ikisini ayrı ayrı talep eder
            ("%15 spor kayıp bonusu, %20 casino kayıp bonusu"). Kapalıyken iki
            kayıp toplanır ve tek oran uygulanır.
          </span>
        </span>
      </label>

      {draft.split_enabled && (
        <div className="grid grid-cols-2 gap-3">
          {VERTICALS.map(({ key, label, hint }) => {
            const own = vertical(key);
            return (
              <div key={key} className="rounded-lg p-3 space-y-3"
                   style={{ background: 'var(--v2-hover, rgba(255,255,255,0.03))', border: '1px solid var(--v2-line, rgba(255,255,255,0.06))' }}>
                <label className="flex items-center gap-2 text-sm font-semibold">
                  <input
                    type="checkbox" disabled={!canWrite} checked={!!own.enabled}
                    onChange={(e) => patchVertical(key, { enabled: e.target.checked })}
                  />
                  {label} kayıp bonusu
                </label>
                <div className="text-[11px] text-slate-500">{hint}</div>

                <div className="flex items-center gap-2">
                  <div className="text-xs font-semibold">Kademeler</div>
                  {canWrite && (
                    <button
                      type="button"
                      className="admin-btn-secondary ml-auto text-xs flex items-center gap-1.5"
                      onClick={() => patchVertical(key, {
                        tiers: [...(own.tiers || []), { min_loss: 0, percent: 0 }],
                      })}
                    >
                      <Plus size={12} /> Kademe Ekle
                    </button>
                  )}
                </div>
                {tierRows(key)}

                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="text-[11px] text-slate-500 block mb-1">Max. bonus (₺)</label>
                    <input
                      type="number" min="0" step="1" disabled={!canWrite}
                      placeholder="Genel" className="admin-input"
                      value={inheritedText(own.max_bonus)}
                      onChange={(e) => patchVertical(key, { max_bonus: parseInherited(e.target.value) })}
                    />
                  </div>
                  <div>
                    <label className="text-[11px] text-slate-500 block mb-1">Min. ödeme (₺)</label>
                    <input
                      type="number" min="0" step="1" disabled={!canWrite}
                      placeholder="Genel" className="admin-input"
                      value={inheritedText(own.min_payout)}
                      onChange={(e) => patchVertical(key, { min_payout: parseInherited(e.target.value) })}
                    />
                  </div>
                  <div>
                    <label className="text-[11px] text-slate-500 block mb-1">Çevrim katsayısı</label>
                    <input
                      type="number" min="0" step="0.5" disabled={!canWrite}
                      placeholder="Genel" className="admin-input"
                      value={inheritedText(own.wager_multiplier)}
                      onChange={(e) => patchVertical(key, { wager_multiplier: parseInherited(e.target.value) })}
                    />
                  </div>
                </div>
                <CoverImageField
                  compact
                  label={`${label} kart görseli`}
                  value={own.cover_image || ''}
                  disabled={!canWrite}
                  onChange={(url) => patchVertical(key, { cover_image: url })}
                  hint="Boşsa yukarıdaki genel kart görseli kullanılır."
                />
                <div className="text-[11px] text-slate-500">
                  Boş bırakılan alan yukarıdaki kurulum geneli ayarı kullanır.
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="flex items-start gap-2 p-3 rounded-lg"
           style={{ background: 'rgba(56,189,248,0.08)', border: '1px solid rgba(56,189,248,0.25)' }}>
        <Info size={16} className="text-cyan-400 mt-0.5 shrink-0" />
        <div className="text-xs text-slate-300">
          Net para girişi tek bir tavandır: spor ve casino ayrı ayrı
          bonuslansa da ikisinin toplamı pencere içinde kasaya giren parayı
          aşamaz. Tavan aşıldığında kayıplar kendi ağırlıklarıyla küçültülür.
        </div>
      </div>

      {canWrite && (
        <button
          type="button"
          className="admin-btn-primary flex items-center gap-2"
          onClick={save}
          disabled={!dirty || saving}
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Kuralları Kaydet
        </button>
      )}
    </div>
  );
}
